<template>
  <div
    v-if="positions && positions.length"
    class="bg-opacity-40 fixed inset-0 z-[9999] flex items-center justify-center p-4"
    :class="mode === 'dark' ? 'bg-white-100' : 'bg-black-100'"
  >
    <div
      class="max-w-[400px] rounded-xl w-full mx-auto"
      :class="mode === 'dark' ? 'bg-dark-70' : 'bg-white-100'"
    >
      <div
        class="flex items-center justify-between p-4 border-b"
        :class="mode === 'dark' ? 'border-dark-50' : ''"
      >
        <h5 class="font-medium text-[17px] leading-none">
          Track position rankings
        </h5>
        <X
          :size="22"
          :stroke-width="1.75"
          @click="$emit('close')"
          class="cursor-pointer"
        />
      </div>
      <div
        class="p-3 rounded-bl-xl rounded-br-xl"
        :class="mode === 'dark' ? 'bg-dark-70' : 'bg-gray-20 bg-opacity-30'"
      >
        <div
          v-if="
            getSortedTrackings(positions)[0]['avg_search_volume'] ||
            getSortedTrackings(positions)[0]['volume_cpc_low'] ||
            getSortedTrackings(positions)[0]['volume_cpc_high']
          "
          class="grid grid-cols-3 gap-1 mb-2"
        >
          <div
            v-if="getSortedTrackings(positions)[0]['avg_search_volume']"
            class="flex items-center border rounded-lg p-2 gap-1 text-sm"
            :class="
              mode === 'dark' ? 'bg-dark-70 border-dark-50' : 'bg-white-100'
            "
          >
            Volume:
            <span class="font-medium">{{
              getSortedTrackings(positions)[0]["avg_search_volume"]
            }}</span>
          </div>
          <div
            v-if="getSortedTrackings(positions)[0]['volume_cpc_low']"
            class="flex items-center border rounded-lg p-2 gap-1 text-sm"
            :class="
              mode === 'dark' ? 'bg-dark-70 border-dark-50' : 'bg-white-100'
            "
          >
            CPC Low:
            <span class="font-medium">{{
              getSortedTrackings(positions)[0]["volume_cpc_low"]
            }}</span>
          </div>
          <div
            v-if="getSortedTrackings(positions)[0]['volume_cpc_high']"
            class="flex items-center border rounded-lg p-2 gap-1 text-sm"
            :class="
              mode === 'dark' ? 'bg-dark-70 border-dark-50' : 'bg-white-100'
            "
          >
            CPC High:
            <span class="font-medium">{{
              getSortedTrackings(positions)[0]["volume_cpc_high"]
            }}</span>
          </div>
        </div>
        <p class="mb-1 text-[15px]">Position</p>
        <canvas width="400" height="400" ref="positionChart"></canvas>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import Chart from "chart.js/auto";
import moment from "moment";
import { X } from "lucide-vue-next";
const props = defineProps({
  positions: {
    type: Array,
    default: () => [],
  },
  mode: {
    type: String,
    default: "light",
  },
});
const positionChart = ref(null);
const genericOptions = {
  fill: false,
  interaction: {
    intersect: false,
  },
  radius: 0,
  parsing: {
    xAxisKey: "tracker_date",
    yAxisKey: "position",
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      boxWidth: 0,
      boxHeight: 0,
      callbacks: {
        title: (item) => {
          return moment(item[0].label).format("D MMM, YYYY");
        },
        label: (item) => {
          return `Position: ${item.formattedValue}`;
        },
      },
    },
  },
  scales: {
    x: {
      ticks: {
        callback: function (value) {
          const label = this.getLabelForValue(value);
          return moment(label).format("D MMM");
        },
      },
    },
    y: {
      reverse: true,
      min: 0,
      ticks: {
        precision: 0,
      },
    },
  },
};
const getSortedTrackings = (tracks) => {
  return tracks.sort(function (a, b) {
    return new Date(b.tracker_date) - new Date(a.tracker_date);
  });
};
const skipped = (ctx, value) => {
  return ctx.p0.skip || ctx.p1.skip ? value : undefined;
};
const down = (ctx, value) => {
  return ctx.p0.parsed.y > ctx.p1.parsed.y ? value : undefined;
};
const config = computed(() => {
  return {
    type: "line",
    data: {
      datasets: [
        {
          label: "",
          data: props.positions
            .map((p) => {
              if (p.position < 0 || !p.position) {
                return {
                  ...p,
                  position: NaN,
                };
              }
              return p;
            })
            .reverse(),
          borderColor: "rgb(75, 192, 192)",
          segment: {
            borderColor: (ctx) =>
              skipped(ctx, "rgb(0,0,0,0.2)") || down(ctx, "rgb(75, 192, 192)"),
            borderDash: (ctx) => skipped(ctx, [6, 6]),
          },
          spanGaps: true,
          pointRadius: 5,
          pointHoverRadius: 8,
        },
      ],
    },
    options: genericOptions,
  };
});
onMounted(() => {
  if (positionChart.value) {
    new Chart(positionChart.value, config.value);
  }
});
</script>
